<template>
    <div class="content-wrapper">
        <app-page-top-section :title="$t('transaction_report')" />

        <div class="row">
            <div class="col-lg-6 mb-3 report-card">
                <div class="card card-with-shadow border-0 h-100">
                    <div class="card-header bg-transparent p-primary d-flex justify-content-between align-items-center">
                        <h5 class="card-title mb-0">{{ $t('transactions_by_category') }}</h5>
                    </div>
                    <div class="card-body position-relative p-primary" :class="{'loading-opacity': loading}">
                        <app-overlay-loader v-if="loading" />
                        <template v-else>
                            <app-chart
                                v-if="hasCategoryData"
                                type="dough-chart"
                                :height="320"
                                :labels="categoryLabels"
                                :data-sets="categoryDataSets"
                                :show-legend="true"
                                :key="categoryChartKey"
                            />
                            <p v-else class="text-center text-muted mb-0">{{ $t('no_data_available') }}</p>

                            <div v-if="hasCategoryData" class="chart-data-list mt-5">
                                <div
                                    class="data-group-item px-0"
                                    v-for="(item, index) in categoryLegend"
                                    :key="`cat-${index}`"
                                >
                                    <span class="square" :style="`background-color: ${item.color}`" />
                                    <span class="value">
                                        {{ item.name }} - {{ item.amount }} ({{ item.count }} {{ $t('transaction') }})
                                    </span>
                                </div>
                            </div>
                        </template>
                    </div>
                </div>
            </div>

            <div class="col-lg-6 mb-3 report-card">
                <div class="card card-with-shadow border-0 h-100">
                <div class="card-header bg-transparent p-primary d-flex justify-content-between align-items-center">
                        <h5 class="card-title mb-0">{{ $t('transactions_by_tag') }}</h5>
                    </div>
                    <div class="card-body position-relative p-primary" :class="{'loading-opacity': loading}">
                        <app-overlay-loader v-if="loading" />
                        <template v-else>
                            <app-chart
                                v-if="hasTagData"
                                type="dough-chart"
                                :height="320"
                                :labels="tagLabels"
                                :data-sets="tagDataSets"
                                :show-legend="true"
                                :key="tagChartKey"
                            />
                            <p v-else class="text-center text-muted mb-0">{{ $t('no_data_available') }}</p>

                            <div v-if="hasTagData" class="chart-data-list mt-5">
                                <div
                                    class="data-group-item px-0"
                                    v-for="(item, index) in tagLegend"
                                    :key="`tag-${index}`"
                                >
                                    <span class="square" :style="`background-color: ${item.color}`" />
                                    <span class="value">
                                        {{ item.name }} - {{ item.amount }} ({{ item.count }} {{ $t('transaction') }})
                                    </span>
                                </div>
                            </div>
                        </template>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import {axiosGet} from "../../../../common/Helper/AxiosHelper";
import {TENANT_BASE_URL} from "../../../../common/Config/UrlHelper";
import {formatCurrency, numberFormatter} from "../../../../common/Helper/Support/SettingsHelper";
import HelperMixin from "../../../../common/Mixin/Global/HelperMixin";

export default {
    name: "FinanceTransactionReport",
    mixins: [HelperMixin],
    data() {
        return {
            loading: false,
            colorPalette: [
                '#5cde64', '#ea833a', '#df4736', '#36a2eb', '#ffcd56',
                '#4dffa2', '#6b5e99', '#ff9f40', '#4bc0c0', '#966ff5',
                '#ff6b6b', '#7dd87d'
            ],
            categoryLabels: [],
            categoryDataSets: [
                {
                    borderWidth: 0,
                    backgroundColor: [],
                    data: []
                }
            ],
            categoryLegend: [],
            categoryChartKey: 0,
            tagLabels: [],
            tagDataSets: [
                {
                    borderWidth: 0,
                    backgroundColor: [],
                    data: []
                }
            ],
            tagLegend: [],
            tagChartKey: 0,
        }
    },
    computed: {
        hasCategoryData() {
            return this.categoryDataSets[0].data.length > 0;
        },
        hasTagData() {
            return this.tagDataSets[0].data.length > 0;
        }
    },
    created() {
        this.fetchReport();
    },
    methods: {
        fetchReport() {
            this.loading = true;
            axiosGet(`${TENANT_BASE_URL}app/finance-transactions/report`)
                .then(({data}) => {
                    const report = data.data || data;
                    this.buildCategoryChart(report.categories || []);
                    this.buildTagChart(report.tags || []);
                })
                .catch(error => {
                    console.error('Error loading transaction report:', error);
                    this.$toastr?.e?.(this.$t('something_went_wrong'));
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        buildCategoryChart(items) {
            this.categoryLabels = items.map(item => this.$displayByLocale(item.name));
            const colors = this.generateColors(items.length);

            this.categoryDataSets[0].data = items.map(item => Number(item.total_amount));
            this.categoryDataSets[0].backgroundColor = colors;

            this.categoryLegend = items.map((item, index) => ({
                name: this.$displayByLocale(item.name),
                amount: formatCurrency(numberFormatter(Number(item.total_amount))),
                count: numberFormatter(Number(item.total_transactions)),
                color: colors[index],
            }));

            this.categoryChartKey += 1;
        },
        buildTagChart(items) {
            this.tagLabels = items.map(item => item.name);
            const colors = this.generateColors(items.length);

            this.tagDataSets[0].data = items.map(item => Number(item.total_amount));
            this.tagDataSets[0].backgroundColor = colors;

            this.tagLegend = items.map((item, index) => ({
                name: item.name,
                amount: formatCurrency(numberFormatter(Number(item.total_amount))),
                count: numberFormatter(Number(item.total_transactions)),
                color: colors[index],
            }));

            this.tagChartKey += 1;
        },
        generateColors(length) {
            const colors = [];
            for (let i = 0; i < length; i++) {
                colors.push(this.colorPalette[i % this.colorPalette.length]);
            }
            return colors;
        },
    }
}
</script>

<style scoped>
@media (max-width: 991.9px) {
    .report-card {
        margin-bottom: 1.5rem !important;
    }
}
</style>
